import apiClient, { getAuthGeneration } from './apiClient';

import { AI_SHARING_VERSION } from '../config/aiSharingPolicy';
export { AI_SHARING_VERSION } from '../config/aiSharingPolicy';
export interface AISharingStatus {
  disclosure_version: string;
  allowed: boolean;
  grant_id: string | null;
  providers: { id: string; name: string; privacy_url: string }[];
  personalization: boolean;
  private_notes: boolean;
  background_learning: boolean;
}
export type AISharingPreferences = Pick<AISharingStatus, 'personalization' | 'private_notes' | 'background_learning'>;
export class AISharingRequired extends Error {
  constructor() { super('Rubikoo AI is off. Enable AI when you are ready, or continue with non-AI event features.'); }
}
export interface AISharingRequest {
  token: string;
  status: AISharingStatus;
  manage: boolean;
  resolve: (allowed: boolean) => void;
  signal?: AbortSignal;
}
const activeWork = new Set<AbortController>();
const grants = new WeakMap<AbortSignal, string>();
export function aiSharingRequestOptions(signal?: AbortSignal) {
  const grantId = signal && grants.get(signal);
  return grantId ? { signal, aiSharingGrantId: grantId } : { signal };
}
export const AI_SHARING_REQUEST = 'ai:sharing-request';
export const AI_SHARING_CHANGED = 'ai:sharing-changed';
const CACHE_TTL_MS = 30_000;
let cacheEpoch = 0;
let cached: { token: string; generation: number; epoch: number; expires: number; status: AISharingStatus } | undefined;
// Memory only, never a security boundary. Every provider request still carries
// the exact server grant, which the API rechecks before and after generation.
export function invalidateAISharing() {
  cached = undefined;
  cacheEpoch += 1;
}
window.addEventListener(AI_SHARING_CHANGED, invalidateAISharing);

async function cachedStatus(token: string, generation: number, signal: AbortSignal, force = false) {
  throwIfChanged(token, signal, generation);
  if (!force && cached?.token === token && cached.generation === generation && cached.epoch === cacheEpoch && cached.expires > Date.now()) return cached.status;
  const epoch = cacheEpoch;
  const status = await getAISharingStatus(signal);
  throwIfChanged(token, signal, generation);
  if (epoch === cacheEpoch) cached = { token, generation, epoch, expires: Date.now() + CACHE_TTL_MS, status };
  return status;
}

export const getAISharingStatus = (signal?: AbortSignal) => apiClient.get<AISharingStatus>('/users/me/ai-sharing', { signal });
export async function getCachedAISharingStatus(signal: AbortSignal, force = false): Promise<AISharingStatus> {
  const token = apiClient.getToken();
  if (!token) throw new AISharingRequired();
  return cachedStatus(token, getAuthGeneration(), signal, force);
}
export async function allowAISharing(version: string, providerIds: string[], preferences: AISharingPreferences, signal?: AbortSignal) {
  const status = await apiClient.post<AISharingStatus>('/users/me/ai-sharing', { disclosure_version: version, provider_ids: providerIds, ...preferences, allow: true }, { signal });
  // Cancel already-started work, not actions still waiting for their first grant.
  for (const controller of activeWork) {
    const previousGrant = grants.get(controller.signal);
    if (previousGrant && previousGrant !== status.grant_id) controller.abort();
  }
  window.dispatchEvent(new Event(AI_SHARING_CHANGED));
  return status;
}
export async function revokeAISharing(signal?: AbortSignal) {
  const status = await apiClient.delete<AISharingStatus>('/users/me/ai-sharing', { signal });
  for (const controller of activeWork) controller.abort();
  window.dispatchEvent(new Event(AI_SHARING_CHANGED));
  return status;
}

function throwIfChanged(token: string, signal?: AbortSignal, generation?: number) {
  if (signal?.aborted || apiClient.getToken() !== token || (generation !== undefined && generation !== getAuthGeneration())) throw new DOMException('AI request cancelled', 'AbortError');
}

function ask(status: AISharingStatus, token: string, manage: boolean, signal?: AbortSignal): Promise<boolean> {
  throwIfChanged(token, signal);
  return new Promise(resolve => {
    let settled = false;
    const finish = (allowed: boolean) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      resolve(allowed);
    };
    const onAbort = () => finish(false);
    signal?.addEventListener('abort', onAbort, { once: true });
    const request: AISharingRequest = { token, status, manage, resolve: finish, signal };
    const event = new CustomEvent<AISharingRequest>(AI_SHARING_REQUEST, { detail: request, cancelable: true });
    // A mounted host acknowledges the request; never hang without one.
    window.dispatchEvent(event);
    if (!event.defaultPrevented) finish(false);
  });
}

export async function manageAISharing(signal?: AbortSignal) {
  const token = apiClient.getToken();
  if (!token) throw new AISharingRequired();
  const generation = getAuthGeneration();
  const status = await getAISharingStatus(signal);
  throwIfChanged(token, signal, generation);
  await ask(status, token, true, signal);
}

// A visible Enable button opens the same disclosure without starting an AI job.
export async function requestAISharing(signal?: AbortSignal): Promise<boolean> {
  const token = apiClient.getToken();
  if (!token) throw new AISharingRequired();
  const generation = getAuthGeneration();
  const status = await getAISharingStatus(signal);
  throwIfChanged(token, signal, generation);
  if (status.allowed && status.disclosure_version === AI_SHARING_VERSION && status.grant_id) return true;
  return ask(status, token, false, signal);
}

export async function withAISharing<T>(work: (signal: AbortSignal) => Promise<T>, options: { signal?: AbortSignal; prompt?: boolean } = {}): Promise<T> {
  const token = apiClient.getToken();
  if (!token) throw new AISharingRequired();
  const generation = getAuthGeneration();
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onAbort, { once: true });
  if (options.signal?.aborted) controller.abort();
  activeWork.add(controller);
  try {
    let status = await cachedStatus(token, generation, controller.signal);
    throwIfChanged(token, controller.signal, generation);
    if (!status.allowed) {
      if (options.prompt === false || !await ask(status, token, false, controller.signal)) throw new AISharingRequired();
      throwIfChanged(token, controller.signal, generation);
      invalidateAISharing();
      status = await cachedStatus(token, generation, controller.signal);
    }
    if (!status.allowed || status.disclosure_version !== AI_SHARING_VERSION || !status.grant_id) throw new AISharingRequired();
    throwIfChanged(token, controller.signal, generation);
    grants.set(controller.signal, status.grant_id);
    const epoch = cacheEpoch;
    const result = await work(controller.signal);
    throwIfChanged(token, controller.signal, generation);
    if (epoch !== cacheEpoch) throw new DOMException('AI sharing changed', 'AbortError');
    return result;
  } catch (error) {
    invalidateAISharing();
    throw error;
  } finally {
    activeWork.delete(controller);
    options.signal?.removeEventListener('abort', onAbort);
  }
}
